import { useState } from 'react';
import { Volume2, VolumeX, RotateCcw, ShieldCheck, ChevronDown, Trophy, Ticket, User } from 'lucide-react';
import { useWallet } from '../../contexts/walletContext';
import './trading.css';

const formatUsd = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

export function EZ5050TradingHeader({
  jackpotAmount = 14303.63,
  demoBalance = 10000,
  realBalance = 0,
  credit = 0,
  accountType = 'DEMO',
  onAccountTypeChange,
  onRefillDemo,
  onOpenJackpot,
  soundEnabled = true,
  onToggleSound,
  hideMobile = false,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDropdownClosing, setIsDropdownClosing] = useState(false);

  const closeDropdown = () => {
    if (isDropdownClosing) return;
    setIsDropdownClosing(true);
    setTimeout(() => {
      setIsDropdownClosing(false);
      setDropdownOpen(false);
    }, 180);
  };

  const toggleDropdown = () => {
    if (dropdownOpen) {
      closeDropdown();
    } else {
      setDropdownOpen(true);
    }
  };
  const { wallet } = useWallet() || {};
  const jackpotTickets = Number(wallet?.spin_and_win?.jackpotTicketCount ?? 0);

  const activeBalance = accountType === 'DEMO' ? demoBalance : realBalance;

  return (
    <header className="select-none z-30 relative"
      style={{ background: '#0f141d', borderBottom: '1px solid #1e2638' }}>
      
      {/* --- DESKTOP HEADER (hidden on mobile, visible on md+) --- */}
      <div className="hidden md:flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <div className="flex items-center space-x-3 shrink-0">
          <div className="w-[120px] h-[auto] flex items-center">
            <img src={require('../../assets/logo.png')} alt="EZ5050" className="w-full" />
          </div>
        </div>

        {/* Jackpot Pill */}
        <button
          onClick={onOpenJackpot}
          className="glow-jackpot-green hover:scale-[1.02] transition-all px-6 py-2 rounded-full flex items-center space-x-2.5 cursor-pointer transform shrink"
          style={{ background: '#121c18' }}
        >
          <span className="text-lg">💵</span>
          <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#34d399' }}>
            JACKPOT:
          </span>
          <span className="font-black text-lg tracking-tight" style={{ color: '#fbbf24' }}>
            {formatUsd(jackpotAmount)}
          </span>
          <Trophy className="w-4 h-4 ml-1 animate-bounce" style={{ color: '#fbbf24' }} />
        </button>
        {/* Right Controls */}
        <div className="flex items-center space-x-3 shrink-0">
          {/* Live Market Badge */}
          <div className="flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ background: '#161f2e', color: '#34d399', border: '1px solid #233147' }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#10b981' }} />
            <span>LIVE MARKET</span>
          </div>

          {/* Sound Toggle */}
          <button
            onClick={() => onToggleSound && onToggleSound(!soundEnabled)}
            className="p-2 rounded-lg transition-colors"
            style={{ background: '#18202e', border: '1px solid #233147', color: soundEnabled ? '#34d399' : '#64748b' }}
            title={soundEnabled ? 'Disable Sound' : 'Enable Sound'}
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>

          {/* Account Selector */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center space-x-2.5 px-3 py-1.5 rounded-lg transition-all"
              style={{ background: '#182130', border: '1px solid #26354d' }}
            >
              <div className="text-left">
                <div className="flex items-center space-x-1">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                    style={{
                      background: accountType === 'DEMO' ? 'rgba(245,158,11,0.2)' : 'rgba(16,185,129,0.2)',
                      color: accountType === 'DEMO' ? '#fbbf24' : '#34d399',
                      border: `1px solid ${accountType === 'DEMO' ? 'rgba(245,158,11,0.3)' : 'rgba(16,185,129,0.3)'}`
                    }}>
                    {accountType}
                  </span>
                  <span className="text-xs inline" style={{ color: '#94a3b8' }}>Balance</span>
                </div>
                <div className="text-sm font-bold text-white tracking-tight">
                  {formatUsd(activeBalance)}
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                style={{ color: '#94a3b8' }} />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 rounded-xl shadow-2xl z-50 p-2 space-y-2"
                style={{ background: '#141b27', border: '1px solid #26354d' }}>
                <div className="text-xs font-semibold px-3 py-1 uppercase tracking-wider" style={{ color: '#94a3b8' }}>
                  Select Account
                </div>

                <div
                  onClick={() => { onAccountTypeChange && onAccountTypeChange('DEMO'); setDropdownOpen(false); }}
                  className="p-2.5 rounded-lg cursor-pointer transition-all flex justify-between items-center"
                  style={{
                    background: accountType === 'DEMO' ? '#1e293b' : 'transparent',
                    border: accountType === 'DEMO' ? '1px solid rgba(245,158,11,0.5)' : '1px solid transparent',
                    color: accountType === 'DEMO' ? 'white' : '#cbd5e1'
                  }}
                >
                  <div>
                    <div className="text-xs font-bold" style={{ color: '#fbbf24' }}>Demo Account</div>
                    <div className="text-sm font-semibold">{formatUsd(demoBalance)}</div>
                  </div>
                  {accountType === 'DEMO' && <ShieldCheck className="w-4 h-4" style={{ color: '#fbbf24' }} />}
                </div>

                <div
                  onClick={() => { onAccountTypeChange && onAccountTypeChange('REAL'); setDropdownOpen(false); }}
                  className="p-2.5 rounded-lg cursor-pointer transition-all flex justify-between items-center"
                  style={{
                    background: accountType === 'REAL' ? '#1e293b' : 'transparent',
                    border: accountType === 'REAL' ? '1px solid rgba(16,185,129,0.5)' : '1px solid transparent',
                    color: accountType === 'REAL' ? 'white' : '#cbd5e1'
                  }}
                >
                  <div>
                    <div className="text-xs font-bold" style={{ color: '#34d399' }}>Real Account</div>
                    <div className="text-sm font-semibold">{formatUsd(realBalance)}</div>
                  </div>
                  {accountType === 'REAL' && <ShieldCheck className="w-4 h-4" style={{ color: '#34d399' }} />}
                </div>

                {accountType === 'DEMO' && (
                  <button
                    onClick={() => { onRefillDemo && onRefillDemo(); setDropdownOpen(false); }}
                    className="w-full flex items-center justify-center space-x-2 py-2 px-3 rounded-lg text-xs font-bold transition-all"
                    style={{ background: 'rgba(245,158,11,0.1)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)' }}
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Refill Demo ($10,000)</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* User Avatar */}
          <div className="flex items-center space-x-2 pl-1">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold cursor-pointer transition-all"
              style={{ background: '#2b3952', border: '2px solid #2b3952', color: '#94a3b8' }}>
              👤
            </div>
          </div>
        </div>
      </div>

      {/* --- MOBILE HEADER (visible on mobile only, hidden on md+) --- */}
      {!hideMobile && (
        <div className="flex md:hidden flex-col pt-2 pb-2 px-3 space-y-1.5 border-b border-white/[0.08]" style={{ background: 'linear-gradient(180deg, #111724 0%, #0b0f17 100%)' }}>
          {/* Row 1: Logo, Ticket, and Profile/Account Pill */}
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="w-[102px] h-auto flex items-center shrink-0">
              <img
                src={require('../../assets/logo.png')}
                alt="EZ5050"
                className="w-full object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
              />
            </div>

            {/* Right Info: Ticket Badge & Account Selector */}
            <div className="flex items-center space-x-2 shrink-0">
              {/* Ticket Badge */}
              <div className="ez-header-ticket-badge">
                <Ticket className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span className="ez-header-ticket-label">Ticket:</span>
                <span className="ez-header-ticket-val">{jackpotTickets}</span>
              </div>

              {/* Account / Tier Selector Pill */}
              <div className="relative">
                <button
                  type="button"
                  onClick={toggleDropdown}
                  className={`ez-header-account-btn ${dropdownOpen ? 'is-open' : ''}`}
                  aria-expanded={dropdownOpen}
                  aria-label="Account options"
                >
                  <span className="ez-header-account-tier">Basic</span>
                  <div className="ez-header-account-avatar">
                    <User className="w-3 h-3 text-slate-200" />
                  </div>
                  <ChevronDown
                    className={`w-3.5 h-3.5 ez-header-account-caret ${dropdownOpen ? 'is-open' : ''}`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Dropdown Menu for account switching */}
          {(dropdownOpen || isDropdownClosing) && (
            <>
              {/* Backdrop */}
              <div
                className={`ez-header-backdrop ${isDropdownClosing ? 'is-closing' : ''}`}
                onClick={closeDropdown}
              />

              <div
                className={`ez-header-dropdown-menu ${isDropdownClosing ? 'is-closing' : ''}`}
              >
                <div className="flex items-center justify-between px-2 pt-1 pb-0.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Switch Account
                  </span>
                  <span className="text-[10px] font-bold text-slate-500">
                    Tier: Basic
                  </span>
                </div>

                <div
                  onClick={() => {
                    onAccountTypeChange && onAccountTypeChange('DEMO');
                    closeDropdown();
                  }}
                  className={`ez-header-account-card ${accountType === 'DEMO' ? 'is-active-demo' : ''}`}
                >
                  <div className="flex items-center space-x-2.5">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 font-black text-xs">
                      $
                    </div>
                    <div>
                      <div className="text-xs font-bold text-amber-400">Demo Account</div>
                      <div className="text-sm font-black text-white font-mono">{formatUsd(demoBalance)}</div>
                    </div>
                  </div>
                  {accountType === 'DEMO' && <ShieldCheck className="w-5 h-5 text-amber-400" />}
                </div>

                <div
                  onClick={() => {
                    onAccountTypeChange && onAccountTypeChange('REAL');
                    closeDropdown();
                  }}
                  className={`ez-header-account-card ${accountType === 'REAL' ? 'is-active-real' : ''}`}
                >
                  <div className="flex items-center space-x-2.5">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-black text-xs">
                      $
                    </div>
                    <div>
                      <div className="text-xs font-bold text-emerald-400">Real Account</div>
                      <div className="text-sm font-black text-white font-mono">{formatUsd(realBalance)}</div>
                    </div>
                  </div>
                  {accountType === 'REAL' && <ShieldCheck className="w-5 h-5 text-emerald-400" />}
                </div>

                {accountType === 'DEMO' && (
                  <button
                    type="button"
                    onClick={() => {
                      onRefillDemo && onRefillDemo();
                      closeDropdown();
                    }}
                    className="ez-header-refill-btn"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Refill Demo ($10,000)</span>
                  </button>
                )}
              </div>
            </>
          )}

          {/* Row 2: Balance & Credit in Sleek Dual Cyber Badges */}
          <div className="grid grid-cols-2 gap-2 pt-0.5">
            <div className="ez-header-stat-card ez-header-stat-card--balance">
              <span className="ez-header-stat-label">Balance</span>
              <span className="ez-header-stat-value text-white">
                {formatUsd(activeBalance)}
              </span>
            </div>

            <div className="ez-header-stat-card ez-header-stat-card--credit">
              <span className="ez-header-stat-label text-amber-400/90">Credit</span>
              <span className="ez-header-stat-value text-amber-400 drop-shadow-[0_0_8px_rgba(245,158,11,0.25)]">
                {formatUsd(credit)}
              </span>
            </div>
          </div>
        </div>
      )}

    </header>
  );
}
